import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import {
  Check,
  Plus,
  ListChecks,
  Trash2,
  AlertCircle,
  Calendar,
  Repeat,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Python weekday convention: 0=Monday ... 6=Sunday
const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const value = `${hh}:${mm}`;
      const hour12 = h % 12 || 12;
      const ampm = h < 12 ? "AM" : "PM";
      opts.push({ value, label: `${hour12}:${mm} ${ampm}` });
    }
  }
  return opts;
})();

const RECURRENCE_TYPES = [
  { value: "none", label: "Doesn't repeat" },
  { value: "daily", label: "Every day" },
  { value: "weekdays", label: "Weekdays (Mon–Fri)" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every other week" },
  { value: "monthly", label: "Monthly" },
  { value: "custom_weekdays", label: "Custom days" },
];

function recurrenceLabel(r) {
  if (!r) return null;
  if (r.type === "daily") return "Daily";
  if (r.type === "weekdays") return "Weekdays";
  if (r.type === "weekly") {
    const d = (r.weekdays || [])[0];
    return `Weekly · ${WEEKDAY_NAMES[d]?.slice(0, 3) || ""}`;
  }
  if (r.type === "biweekly") {
    const d = (r.weekdays || [])[0];
    return `Biweekly · ${WEEKDAY_NAMES[d]?.slice(0, 3) || ""}`;
  }
  if (r.type === "monthly") return `Monthly · day ${r.day_of_month}`;
  if (r.type === "custom_weekdays") {
    const days = (r.weekdays || []).map((d) => WEEKDAY_LABELS[d]).join("");
    return `Custom · ${days}`;
  }
  return null;
}

function formatDue(d) {
  if (!d) return null;
  const dt = new Date(d + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((dt - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1 && diff < 7)
    return dt.toLocaleDateString([], { weekday: "long" });
  return dt.toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatTime(t) {
  if (!t) return null;
  const [h, m] = t.split(":");
  const d = new Date();
  d.setHours(Number(h), Number(m), 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
}

function isOverdue(t) {
  if (t.completed || !t.due_date) return false;
  const today = new Date().toISOString().slice(0, 10);
  return t.due_date < today;
}

function ScheduleEditor({ value, onChange }) {
  const recurrence = value.recurrence || { type: "none" };
  const r = recurrence;
  const setType = (type) => {
    if (type === "none") return onChange({ ...value, recurrence: null });
    const today = new Date();
    const next = { type };
    if (type === "weekly" || type === "biweekly") next.weekdays = [today.getDay() === 0 ? 6 : today.getDay() - 1];
    if (type === "monthly") next.day_of_month = today.getDate();
    if (type === "custom_weekdays") next.weekdays = [];
    onChange({ ...value, recurrence: next });
  };
  const setWeekday = (idx) => {
    const cur = r.weekdays || [];
    if (r.type === "custom_weekdays") {
      const exists = cur.includes(idx);
      onChange({
        ...value,
        recurrence: {
          ...r,
          weekdays: exists ? cur.filter((d) => d !== idx) : [...cur, idx].sort(),
        },
      });
    } else {
      onChange({ ...value, recurrence: { ...r, weekdays: [idx] } });
    }
  };

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
            Due
          </div>
          <input
            data-testid="todo-due-date"
            type="date"
            value={value.due_date || ""}
            onChange={(e) =>
              onChange({ ...value, due_date: e.target.value || null })
            }
            className="w-full h-10 px-3 rounded-lg bg-[#171514] border border-[#33302C] text-[#EAE4DB] text-sm focus:outline-none focus:border-[#D6A85B]"
          />
        </div>
        <div>
          <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
            Time
          </div>
          <select
            data-testid="todo-due-time"
            value={value.due_time || ""}
            disabled={!value.due_date}
            onChange={(e) =>
              onChange({ ...value, due_time: e.target.value || null })
            }
            className="w-full h-10 px-3 rounded-lg bg-[#171514] border border-[#33302C] text-[#EAE4DB] text-sm focus:outline-none focus:border-[#D6A85B] disabled:opacity-40"
          >
            <option value="">No time</option>
            {TIME_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
          Repeat
        </div>
        <select
          data-testid="todo-recurrence-type"
          value={r.type || "none"}
          onChange={(e) => setType(e.target.value)}
          className="w-full h-10 px-3 rounded-lg bg-[#171514] border border-[#33302C] text-[#EAE4DB] text-sm focus:outline-none focus:border-[#D6A85B]"
        >
          {RECURRENCE_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {(r.type === "weekly" || r.type === "biweekly") && (
        <div>
          <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
            On
          </div>
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAY_LABELS.map((lbl, i) => {
              const active = (r.weekdays || [])[0] === i;
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`weekday-pick-${i}`}
                  onClick={() => setWeekday(i)}
                  className="h-9 rounded-md text-xs font-semibold transition"
                  style={{
                    backgroundColor: active ? "#D6A85B" : "#171514",
                    color: active ? "#171514" : "#A6A098",
                    border: `1px solid ${active ? "#D6A85B" : "#33302C"}`,
                  }}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {r.type === "custom_weekdays" && (
        <div>
          <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
            On
          </div>
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAY_LABELS.map((lbl, i) => {
              const active = (r.weekdays || []).includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`weekday-pick-${i}`}
                  onClick={() => setWeekday(i)}
                  className="h-9 rounded-md text-xs font-semibold transition"
                  style={{
                    backgroundColor: active ? "#D6A85B" : "#171514",
                    color: active ? "#171514" : "#A6A098",
                    border: `1px solid ${active ? "#D6A85B" : "#33302C"}`,
                  }}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {r.type === "monthly" && (
        <div>
          <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
            Day of month
          </div>
          <input
            data-testid="todo-day-of-month"
            type="number"
            min={1}
            max={31}
            value={r.day_of_month || 1}
            onChange={(e) =>
              onChange({
                ...value,
                recurrence: { ...r, day_of_month: Math.max(1, Math.min(31, Number(e.target.value))) },
              })
            }
            className="w-full h-10 px-3 rounded-lg bg-[#171514] border border-[#33302C] text-[#EAE4DB] text-sm focus:outline-none focus:border-[#D6A85B]"
          />
        </div>
      )}
    </div>
  );
}

export default function TodoPanel({ family }) {
  const qc = useQueryClient();
  const { data: todos = [] } = useQuery({
    queryKey: ["todos"],
    queryFn: endpoints.todos,
    refetchInterval: 1000 * 60 * 5,
  });

  const familyById = useMemo(
    () => Object.fromEntries(family.map((f) => [f.id, f])),
    [family]
  );

  const [text, setText] = useState("");
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [filter, setFilter] = useState(null);
  const [schedule, setSchedule] = useState({ due_date: null, due_time: null, recurrence: null });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const hasSchedule = schedule.due_date || schedule.recurrence;

  const createMut = useMutation({
    mutationFn: endpoints.createTodo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["todos"] });
      setText("");
      setAssigneeIds([]);
      setSchedule({ due_date: null, due_time: null, recurrence: null });
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => endpoints.updateTodo(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["todos"] }),
  });

  const delMut = useMutation({
    mutationFn: endpoints.deleteTodo,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["todos"] }),
  });

  function addTodo(e) {
    e?.preventDefault();
    if (!text.trim()) return toast.error("Add a task");
    createMut.mutate({
      title: text.trim(),
      assignee_ids: assigneeIds,
      due_date: schedule.due_date,
      due_time: schedule.due_time,
      recurrence: schedule.recurrence,
    });
  }

  function toggleAssignee(id) {
    setAssigneeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const visible = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const rank = (t) => {
      if (t.completed) return 5;
      if (!t.due_date) return 3;
      if (t.due_date < todayIso) return 0; // overdue (most overdue first)
      if (t.due_date === todayIso) return 1; // due today
      return 2; // upcoming
    };
    const filtered = todos.filter(
      (t) => !filter || (t.assignee_ids || []).includes(filter)
    );
    return [...filtered].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      // Within group: sort by due_date asc (overdue earliest first, upcoming nearest first)
      if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      // Both no due date — preserve created_at order
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
  }, [todos, filter]);
  const remaining = visible.filter((t) => !t.completed).length;
  const overdueCount = visible.filter(isOverdue).length;
  const dueTodayCount = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return visible.filter((t) => !t.completed && t.due_date === todayIso).length;
  }, [visible]);

  return (
    <div
      data-testid="todo-card"
      className="wood-panel rounded-[2rem] border p-6 lg:p-8 h-full flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <ListChecks size={22} className="text-[#6B8E7B]" />
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#EAE4DB]">
            To-do
          </h2>
        </div>
        <div className="text-xs font-mono uppercase tracking-[0.15em] text-[#A6A098] flex items-center gap-3">
          {overdueCount > 0 && (
            <span data-testid="overdue-count" className="text-[#C85A5A]">
              {overdueCount} overdue
            </span>
          )}
          {dueTodayCount > 0 && (
            <span data-testid="due-today-count" className="text-[#D6A85B]">
              {dueTodayCount} today
            </span>
          )}
          <span>{remaining} left</span>
        </div>
      </div>

      {/* Family filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          data-testid="filter-all"
          onClick={() => setFilter(null)}
          className={`h-10 px-4 rounded-full border text-sm uppercase tracking-wider transition ${
            !filter
              ? "bg-[#EAE4DB] text-[#171514] border-[#EAE4DB] font-semibold"
              : "bg-transparent text-[#A6A098] border-[#33302C] hover:text-[#EAE4DB]"
          }`}
        >
          All
        </button>
        {family.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              data-testid={`filter-${f.role}`}
              onClick={() => setFilter(active ? null : f.id)}
              className="h-10 px-4 rounded-full border text-sm uppercase tracking-wider font-semibold transition flex items-center gap-2"
              style={{
                backgroundColor: active ? f.color : "transparent",
                color: active ? "#171514" : "#A6A098",
                borderColor: active ? f.color : "#33302C",
              }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: active ? "#171514" : f.color }}
              />
              {f.name}
            </button>
          );
        })}
      </div>

      {/* Add form */}
      <form onSubmit={addTodo} className="mb-3">
        <div className="flex gap-2">
          <input
            data-testid="todo-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a task…"
            className="flex-1 h-12 px-4 rounded-full bg-[#171514] border border-[#33302C] text-[#EAE4DB] placeholder:text-[#5a544e] focus:outline-none focus:border-[#D6A85B]"
          />
          <div className="flex gap-1.5">
            {family.map((f) => {
              const active = assigneeIds.includes(f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`assign-${f.role}`}
                  aria-pressed={active}
                  onClick={() => toggleAssignee(f.id)}
                  className="h-12 w-12 rounded-full border-2 flex items-center justify-center transition shrink-0"
                  style={{
                    borderColor: active ? f.color : "#33302C",
                    backgroundColor: active ? f.color : "transparent",
                  }}
                  title={f.name}
                  aria-label={`Assign to ${f.name}`}
                >
                  <span
                    className="text-xs font-bold"
                    style={{ color: active ? "#171514" : f.color }}
                  >
                    {f.name[0]}
                  </span>
                </button>
              );
            })}
          </div>
          <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="schedule-btn"
                className="h-12 w-12 rounded-full border-2 flex items-center justify-center transition shrink-0"
                style={{
                  borderColor: hasSchedule ? "#D6A85B" : "#33302C",
                  backgroundColor: hasSchedule ? "#D6A85B" : "transparent",
                  color: hasSchedule ? "#171514" : "#A6A098",
                }}
                aria-label="Schedule"
              >
                <Calendar size={18} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="bg-[#23211F] border-[#33302C] text-[#EAE4DB] w-80"
            >
              <ScheduleEditor value={schedule} onChange={setSchedule} />
              {hasSchedule && (
                <button
                  type="button"
                  data-testid="clear-schedule"
                  onClick={() =>
                    setSchedule({ due_date: null, due_time: null, recurrence: null })
                  }
                  className="mt-3 text-xs font-mono uppercase tracking-[0.15em] text-[#A6A098] hover:text-[#C85A5A] flex items-center gap-1.5"
                >
                  <X size={12} /> Clear schedule
                </button>
              )}
            </PopoverContent>
          </Popover>
          <button
            type="submit"
            data-testid="add-todo-btn"
            className="h-12 w-12 rounded-full bg-[#D6A85B] text-[#171514] flex items-center justify-center hover:bg-[#c79a4f] transition shrink-0"
            aria-label="Add"
          >
            <Plus size={20} />
          </button>
        </div>
        {hasSchedule && (
          <div
            data-testid="schedule-summary"
            className="mt-2 ml-1 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.12em] text-[#D6A85B]"
          >
            {schedule.due_date && (
              <span className="flex items-center gap-1">
                <Calendar size={11} /> {formatDue(schedule.due_date)}
              </span>
            )}
            {schedule.recurrence && (
              <span className="flex items-center gap-1">
                <Repeat size={11} /> {recurrenceLabel(schedule.recurrence)}
              </span>
            )}
          </div>
        )}
      </form>

      {/* List */}
      <ul
        data-testid="todo-list"
        className="space-y-2 flex-1 min-h-0 overflow-y-auto no-scrollbar pr-1"
      >
        {visible.length === 0 && (
          <li className="text-[#A6A098] text-sm">Nothing here. Enjoy the quiet.</li>
        )}
        {visible.map((t) => {
          const assignees = (t.assignee_ids || [])
            .map((id) => familyById[id])
            .filter(Boolean);
          const primary = assignees[0];
          const overdue = isOverdue(t);
          const todayIso = new Date().toISOString().slice(0, 10);
          const dueToday = !t.completed && t.due_date === todayIso;
          const rLabel = recurrenceLabel(t.recurrence);
          const dueLabel = formatDue(t.due_date);
          return (
            <li
              key={t.id}
              data-testid={`todo-row-${t.id}`}
              className={`group flex items-center gap-3 p-3 rounded-2xl border transition ${
                t.completed
                  ? "bg-transparent border-transparent opacity-60"
                  : overdue
                  ? "bg-[#1f1715] border-[#C85A5A]/40 hover:border-[#C85A5A]"
                  : dueToday
                  ? "bg-[#1f1c15] border-[#D6A85B]/40 hover:border-[#D6A85B]"
                  : "bg-[#171514] border-[#33302C] hover:border-[#5a544e]"
              }`}
            >
              <button
                data-testid={`toggle-todo-${t.id}`}
                onClick={() =>
                  updateMut.mutate({ id: t.id, payload: { completed: !t.completed } })
                }
                className="w-11 h-11 rounded-full border-2 flex items-center justify-center transition shrink-0"
                style={{
                  borderColor: t.completed
                    ? primary?.color || "#6B8E7B"
                    : overdue
                    ? "#C85A5A"
                    : dueToday
                    ? "#D6A85B"
                    : "#33302C",
                  backgroundColor: t.completed ? primary?.color || "#6B8E7B" : "transparent",
                }}
                aria-label="Toggle complete"
              >
                {t.completed && <Check size={20} className="text-[#171514]" strokeWidth={3} />}
              </button>
              <div className="flex-1 min-w-0">
                <div
                  className={`text-lg ${
                    t.completed ? "line-through text-[#A6A098]" : "text-[#EAE4DB]"
                  }`}
                >
                  {t.title}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs font-mono uppercase tracking-[0.15em]">
                  {assignees.length > 0 && (
                    <span
                      data-testid={`assignees-${t.id}`}
                      className="flex items-center gap-1.5"
                    >
                      {assignees.map((a, i) => (
                        <span key={a.id} className="flex items-center gap-1">
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: a.color }}
                          />
                          <span style={{ color: a.color }}>{a.name}</span>
                          {i < assignees.length - 1 && (
                            <span className="text-[#5a544e]">·</span>
                          )}
                        </span>
                      ))}
                    </span>
                  )}
                  {dueLabel && (
                    <span
                      data-testid={`due-${t.id}`}
                      className="flex items-center gap-1"
                      style={{
                        color: overdue
                          ? "#C85A5A"
                          : dueToday
                          ? "#D6A85B"
                          : "#A6A098",
                      }}
                    >
                      <Calendar size={10} />{" "}
                      {overdue
                        ? `Overdue · ${dueLabel}`
                        : dueToday
                        ? "Today"
                        : dueLabel}
                      {t.due_time ? ` · ${formatTime(t.due_time)}` : ""}
                    </span>
                  )}
                  {rLabel && (
                    <span className="flex items-center gap-1 text-[#A6A098]">
                      <Repeat size={10} /> {rLabel}
                    </span>
                  )}
                </div>
              </div>
              {t.priority === "urgent" && (
                <AlertCircle size={18} className="text-[#C85A5A] shrink-0" />
              )}
              <button
                data-testid={`delete-todo-${t.id}`}
                onClick={() => {
                  if (t.recurrence) {
                    setPendingDelete(t);
                  } else {
                    delMut.mutate(t.id);
                  }
                }}
                className="h-9 w-9 rounded-full text-[#A6A098]/60 hover:text-[#C85A5A] hover:bg-[#2D2A28] flex items-center justify-center transition shrink-0"
                aria-label="Delete"
              >
                <Trash2 size={16} />
              </button>
            </li>
          );
        })}
      </ul>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent className="bg-[#23211F] border-[#33302C] text-[#EAE4DB]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Delete recurring task?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A6A098]">
              &ldquo;{pendingDelete?.title}&rdquo; repeats{" "}
              <span className="text-[#EAE4DB]">
                {recurrenceLabel(pendingDelete?.recurrence)?.toLowerCase()}
              </span>
              . Deleting will remove it permanently &mdash; it won&rsquo;t come back on the next cycle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-recurring"
              className="bg-transparent border-[#33302C] text-[#EAE4DB] hover:bg-[#2D2A28] hover:text-[#EAE4DB]"
            >
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-recurring"
              onClick={() => {
                if (pendingDelete) delMut.mutate(pendingDelete.id);
                setPendingDelete(null);
              }}
              className="bg-[#C85A5A] text-[#171514] hover:bg-[#b04a4a]"
            >
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
